import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { API } from "@/lib/api";
import Logo from "@/components/Logo";
import {
  ShieldCheck, FolderOpen, Camera, BookOpen, Compass, MessageSquare,
  PenLine, Mail, FileCheck, Fingerprint, Image as ImgIcon, Share2,
  Sparkles, ListChecks, Phone, Receipt, Brain, ArrowRight, Download,
  Check, Clock, Calculator, AlertCircle, Copy, Facebook,
  MessageCircle, Twitter, X as XIcon, Mic,
} from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import Navbar from "@/components/Navbar";
import { COMPASS_BODY, PROVE_HERO_CALLOUT } from "@/data/featuresCopy";

/**
 * Features page, the canonical "everything TenantTrail does" page,
 * grouped by jobs-to-be-done. Designed to be linkable from ads, social
 * bios, founder posts and community posts. Mirrors the one-pager PDF
 * (kept in sync, see /app/backend/features_pdf.py GROUPS).
 *
 * Tone: warm, accountability-framed, no anti-agent language. Hero callouts
 * for the standout differentiators (Photo Trail, Photo Compare, AI letters).
 */

const HERO_FEATURES = [
  {
    icon: Camera,
    name: "Photo Trail",
    blurb: "Monthly photo check-ins of your property, dated and locked. Build a quiet timeline of cleanliness and condition that nobody can argue with later.",
    badge: "Newest",
  },
  {
    icon: ImgIcon,
    name: "Photo Compare",
    blurb: "Drop your dated photo next to the agent's claim photo. Compass writes a plain-English comparison and drops it into your reply letter as an attachment or inline paragraph.",
    badge: "Newest",
  },
  {
    icon: PenLine,
    name: "AI letter drafting (with iterative refinement)",
    blurb: "Pick a template, Compass writes the letter citing the relevant Act sections. Refine iteratively, 'add a 14-day deadline', 'soften the tone', until it sounds like you.",
  },
  {
    icon: Mic,
    name: "Voice notes with tamper-evident share links",
    blurb: "One-tap recording for the moments that matter: inspections, agent visits, illegal entry. We keep the original audio. You can transcribe on demand and generate a public share link the recipient opens in any browser. SHA-256 fingerprint included so it can't be edited after the fact.",
    badge: "New",
  },
  {
    icon: Compass,
    name: "State-aware AI (Compass)",
    blurb: "Tell Compass once whether you're in NSW, VIC, QLD, WA, SA, TAS, ACT or NT. Every answer cites the exact section of YOUR state's Residential Tenancies Act, plus the right tribunal (NCAT, VCAT, QCAT, SACAT, ACAT, etc). No generic, lifted-from-a-US-blog answers.",
  },
  {
    icon: FileCheck,
    name: "Tribunal-ready evidence bundle",
    blurb: "One-tap PDF export. Cover page, indexed contents, every photo with a SHA-256 fingerprint, voice notes with the transcript and a QR code the tribunal can scan to play the original audio. Walk in with one document, not a shoebox of screenshots.",
  },
  {
    icon: Fingerprint,
    name: "SHA-256 tamper-check on every photo",
    blurb: "Every photo you upload gets a unique fingerprint. If the file is edited later, even one pixel, the fingerprint changes and we can prove the original wasn't touched. Members of the public can verify any fingerprint on /verify without an account.",
  },
  {
    icon: ListChecks,
    name: "Chronology you can lock before tribunal",
    blurb: "Drop events on a calendar, what happened, who said what, what you sent. Compass writes the tribunal-ready chronology paragraph for you, then you lock it so it can't be questioned later.",
  },
  {
    icon: Brain,
    name: "Tenant Brain (free for anyone)",
    blurb: "A growing library of real-world Australian tenant Q&As, plain-English, state-tagged, no login. Read the ones that match your situation before you ever spend a cent with us.",
  },
];

const GROUPS = [
  {
    key: "organise",
    label: "Organise",
    tagline: "Everything in one place, dated, searchable, never lost.",
    icon: FolderOpen,
    items: [
      { icon: FolderOpen, name: "Document vault", body: "Drop in 100 photos, emails and PDFs at once. Lease, bond receipt, entry condition, auto-sorted by type and date." },
      { icon: Camera, name: "Photo Trail", body: "Monthly photo check-ins. Each entry is dated and tamper-evident so a year later, the timeline is still rock-solid." },
      { icon: Phone, name: "Contacts ledger", body: "Every agent / landlord / tradie phone call logged with the date, who you spoke to and what they said." },
      { icon: Mic, name: "Voice notes (audio kept)", body: "Tap the floating mic from anywhere in the app to record what just happened. We keep the original .webm audio in your vault. Transcribe on demand when you want it as text." },
      { icon: Receipt, name: "Rent + bond tracker", body: "Receipts in, rent increases tracked, bond status visible at a glance. No more hunting through emails." },
    ],
  },
  {
    key: "understand",
    label: "Understand",
    tagline: "Plain-English help that knows your state's residential tenancy law.",
    icon: Compass,
    items: [
      { icon: Compass, name: "Compass AI", body: COMPASS_BODY },
      { icon: Brain, name: "Tenant Brain", body: "Searchable library of real-world tenant Q&As, most common questions already answered, free to read, no account needed." },
      { icon: FileCheck, name: "Inspection report extraction", body: "Upload an entry or exit condition report. We pull out the deductions and dispute points into a clear list." },
    ],
  },
  {
    key: "respond",
    label: "Respond",
    tagline: "Calm, polished letters and emails, drafted in seconds, refined as much as you like.",
    icon: PenLine,
    items: [
      { icon: PenLine, name: "AI letter drafting", body: "Push back, calmly and correctly. Compass drafts the reply and quotes the rule that backs you up, ready to send to your agent." },
      { icon: Sparkles, name: "Suggested refinements", body: "Tap a chip, 'add a 14-day deadline', 'cite section 217', 'soften the tone' and Compass rewrites instantly." },
      { icon: ImgIcon, name: "Photo Compare", body: "Place your dated photo next to the agent's claim. AI writes a plain-English comparison you can drop into a reply." },
      { icon: Mail, name: "Email direct to agent", body: "Send the letter straight from the app. Audit trail, CC yourself, track when it's opened." },
    ],
  },
  {
    key: "prove",
    label: "Prove",
    tagline: "Tamper-evident evidence, organised the way tribunals expect.",
    icon: ShieldCheck,
    heroCallout: PROVE_HERO_CALLOUT,
    items: [
      { icon: ListChecks, name: "Timeline / chronology", body: "Every event in date order, what happened, what you sent, who replied. Lock it before tribunal so it can't be questioned." },
      { icon: Fingerprint, name: "SHA-256 fingerprints", body: "Every photo gets a tamper-evident fingerprint. If it's edited later, the fingerprint won't match, so the change is easy to demonstrate." },
      { icon: FileCheck, name: "Tribunal evidence bundle", body: "One-click PDF export, cover page, indexed contents, every document with date stamps and fingerprints. Voice notes get their own page with the transcript, SHA-256 and a QR code to play the audio." },
      { icon: Share2, name: "Voice note share link", body: "Generate a tamper-evident /v/ link for any voice note. Send to a friend, family member, support worker or your Tenants' Union. SHA-256 fingerprint shown publicly so the recipient knows the audio hasn't been edited. Revoke any time." },
      { icon: Share2, name: "Verified anonymous shares", body: "Public, watermarked share pages for tenancy Facebook groups, agent replies, journalists. No personal info ever shown." },
    ],
  },
];

const PRICE_STRIP = [
  { id: "free", name: "Free", price: "$0", sub: "5 AI Q&As / mo · 1 case" },
  { id: "vault", name: "Vault", price: "$9.99 / mo", sub: "10 AI Q&As / mo · 5 GB" },
  { id: "compass", name: "Compass", price: "$19.99 / mo", sub: "100 AI Q&As / mo · voice notes · letters", badge: "Most popular" },
  { id: "dispute", name: "Dispute Mode", price: "$69 one-off", sub: "500 AI Q&As · 30 days" },
  { id: "pro", name: "Pro", price: "$39.99 / mo", sub: "750 AI Q&As / mo · 50 GB · priority" },
];

export default function Features() {
  return (
    <div className="min-h-screen bg-[#FAF9F4]" data-testid="features-page">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="border-b border-[#E5E4DE] bg-white">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
            <div className="label-mono mb-3">Features · everything we do</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0D2B52] leading-[1.05] mb-4 max-w-4xl">
              Your whole renting arsenal, on one page.
            </h1>
            <p className="text-[15px] sm:text-base text-[#5a6b7a] max-w-2xl leading-relaxed mb-6">
              TenantTrail covers the whole tenancy, move-in to move-out. Keep every record straight. Know the laws, acts and codes that protect you. Respond to your agent with confidence. And if it ever goes all the way, walk into tribunal ready. Here is everything it does.
            </p>
            <div className="flex flex-wrap items-center gap-3" data-testid="features-hero-cta">
              <Link to="/register" className="btn-teal text-sm inline-flex items-center gap-1.5" data-testid="features-signup-btn">
                Start free <ArrowRight size={13} strokeWidth={1.75} />
              </Link>
              <a
                href={`${API}/features/onepager.pdf`}
                target="_blank" rel="noopener noreferrer"
                className="btn-ghost text-sm inline-flex items-center gap-1.5 border border-[#E5E4DE]"
                data-testid="features-pdf-btn"
              >
                <Download size={13} strokeWidth={1.75} /> Download fact sheet PDF
              </a>
              <Link to="/pricing" className="text-sm text-[#1B6C62] hover:underline" data-testid="features-pricing-link">
                See pricing →
              </Link>
            </div>
          </div>
        </section>

        {/* Hero features (differentiators) */}
        <section className="bg-[#FAF6E8] border-b border-[#C9A961]/30">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} strokeWidth={1.5} className="text-[#8A6D3B]" />
              <span className="label-mono text-[10px] text-[#8A6D3B]">What makes TenantTrail different</span>
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0D2B52] mb-6 max-w-3xl">
              Nine things you won&rsquo;t find anywhere else.
            </h2>
            <div className="grid md:grid-cols-3 gap-5">
              {HERO_FEATURES.map(({ icon: Icon, name, blurb, badge }, i) => (
                <div key={name} className="bg-white border border-[#E5E4DE] p-5 relative" data-testid={`features-hero-${i}`}>
                  {badge && (
                    <span className="absolute top-3 right-3 text-[9px] font-mono uppercase tracking-wide bg-[#C9A961] text-white px-1.5 py-0.5">{badge}</span>
                  )}
                  <Icon size={22} strokeWidth={1.5} className="text-[#1B6C62] mb-3" />
                  <h3 className="font-serif text-lg text-[#0D2B52] mb-1.5 leading-tight">{name}</h3>
                  <p className="text-[13px] text-[#1A1A1A] leading-relaxed">{blurb}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Job-to-be-done groups */}
        <section>
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0D2B52] mb-2">The full toolkit</h2>
            <p className="text-[14px] text-[#5a6b7a] mb-8 max-w-2xl">
              Grouped by what you&apos;re trying to do, not by feature name.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {GROUPS.map(({ key, label, tagline, icon: GroupIcon, items, heroCallout }) => (
                <div key={key} className="bg-white border border-[#E5E4DE] p-6" data-testid={`features-group-${key}`}>
                  <div className="flex items-center gap-2.5 mb-1">
                    <GroupIcon size={18} strokeWidth={1.5} className="text-[#1B6C62]" />
                    <h3 className="font-serif text-2xl text-[#0D2B52]">{label}</h3>
                  </div>
                  <p className="text-[13px] text-[#5a6b7a] italic mb-5">{tagline}</p>
                  {heroCallout && (
                    <div
                      className="border-l-4 border-l-[#C9A961] bg-[#FAF6E8] p-4 mb-5"
                      data-testid={`features-group-${key}-hero`}
                    >
                      <div className="font-serif text-[15px] text-[#0D2B52] mb-1.5 leading-snug">
                        {heroCallout.heading}
                      </div>
                      <p className="text-[12.5px] text-[#1A1A1A] leading-relaxed">
                        {heroCallout.body}
                      </p>
                    </div>
                  )}
                  <ul className="space-y-4">
                    {items.map(({ icon: ItemIcon, name, body }) => (
                      <li key={name} className="flex items-start gap-3" data-testid={`features-item-${name.replace(/\s+/g, '-').toLowerCase()}`}>
                        <ItemIcon size={16} strokeWidth={1.5} className="text-[#1B6C62] mt-1 shrink-0" />
                        <div>
                          <div className="font-serif text-[15px] text-[#0D2B52] leading-tight mb-0.5">{name}</div>
                          <div className="text-[12.5px] text-[#1A1A1A] leading-relaxed">{body}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing snapshot */}
        <section className="bg-white border-y border-[#E5E4DE]">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12">
            <div className="label-mono mb-2">Pricing · AUD</div>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#0D2B52] mb-2">Pick the plan for where you are right now</h2>
            <p className="text-[13px] text-[#5a6b7a] mb-6 max-w-2xl">
              All AI allowances are fair-use bounded with a daily cap to keep things fast for everyone. <Link to="/pricing" className="underline text-[#1B6C62]">Full plan comparison →</Link>
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="features-price-strip">
              {PRICE_STRIP.map((p, i) => (
                <Link
                  key={p.name}
                  to={`/pricing#${p.id}`}
                  className={`group block border ${p.badge ? "border-[#1B6C62] bg-[#E6F5F2] hover:bg-[#d8efe9]" : "border-[#E5E4DE] bg-white hover:border-[#1B6C62] hover:bg-[#FAFCFB]"} p-4 relative transition-colors`}
                  data-testid={`features-price-${i}`}
                  aria-label={`See ${p.name} plan details and checkout`}
                >
                  {p.badge && (
                    <div className="text-[9px] font-mono uppercase tracking-wide text-[#1B6C62] mb-1">{p.badge}</div>
                  )}
                  <div className="font-serif text-[15px] text-[#0D2B52] mb-0.5">{p.name}</div>
                  <div className="font-mono text-[16px] text-[#1B6C62] font-bold mb-1">{p.price}</div>
                  <div className="text-[11px] text-[#5a6b7a] leading-snug mb-3">{p.sub}</div>
                  <div className="text-[11px] font-mono text-[#1B6C62] inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
                    {p.id === "free" ? "Start free" : p.id === "dispute" ? "Get Dispute Mode" : `Choose ${p.name}`}
                    <ArrowRight size={11} strokeWidth={1.75} />
                  </div>
                </Link>
              ))}
            </div>
            <p className="text-[11px] text-[#6B7280] italic mt-4">
              Genuinely heavy power user? <a href="mailto:hello@tenanttrail.net" className="underline hover:text-[#1B6C62]">Email us</a> for a fair-use bump above the standard plan caps.
            </p>
          </div>
        </section>

        {/* ROI Calculator, "What does going it alone really cost?" */}
        <div id="worth-it" />
        <ROISection />

        {/* CTA */}
        <section className="bg-[#0D2B52] text-white">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14 text-center">
            <h2 className="font-serif text-3xl sm:text-4xl mb-3 leading-tight">Renting in Australia? Start free, no card needed.</h2>
            <p className="text-[15px] opacity-90 mb-7 max-w-xl mx-auto leading-relaxed">
              5 AI questions on us. Keep your lease + bond receipt safe forever, completely free.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/register" className="bg-white text-[#0D2B52] px-6 py-3 text-sm font-medium hover:bg-[#FAF9F4] transition-colors inline-flex items-center gap-1.5" data-testid="features-cta-signup">
                Start free <ArrowRight size={13} strokeWidth={1.75} />
              </Link>
              <a
                href={`${API}/features/onepager.pdf`}
                target="_blank" rel="noopener noreferrer"
                className="text-white/90 hover:text-white underline-offset-4 hover:underline text-sm inline-flex items-center gap-1.5"
                data-testid="features-cta-pdf"
              >
                <Download size={13} strokeWidth={1.75} /> Fact sheet PDF
              </a>
            </div>
          </div>
        </section>
      
      <SiteFooter />
      </main>
    </div>
  );
}


// ---------- ROI calculator ----------
// A calm, honest "what does going it alone really cost?" widget. We do NOT
// promise a financial outcome, TenantTrail can't and shouldn't claim
// you'll win your bond back. We just put the time/cost trade-off side by
// side so the visitor sees what they'd otherwise be signing up for.
//
// Numbers are deliberately conservative, drawn from publicly-quoted AU
// tenant-advocate rates and our own average usage data. If the user picks
// a higher dispute amount, advocate time scales because preparation
// scales, that's true in practice too.

const HOURLY_TIME_VALUE = 30;  // median AU after-tax hourly wage, rounded

function ROISection() {
  // Default to a typical bond-claim amount most renters worry about
  const [amount, setAmount] = useState(1200);
  const [disputeType, setDisputeType] = useState("bond");
  const [showShare, setShowShare] = useState(false);

  // Estimate hours of admin saved + advocate hours needed by dispute size.
  // Bond disputes scale slower than general legal matters.
  const estimates = useMemo(() => {
    // Scale: very small (<$500) ~ 4 hours; small (<$2000) ~ 8h; medium (<$5000) ~ 12h; large 15h
    let diyHours;
    if (amount < 500) diyHours = 4;
    else if (amount < 2000) diyHours = 8;
    else if (amount < 5000) diyHours = 12;
    else diyHours = 15;

    // Advocate prep hours (private tenancy advocate rates ~ $200-280/hour AU)
    const advocateHours = Math.min(2 + Math.ceil(amount / 600), 8);  // 2-8 hours typical
    const advocateRate = 250;
    const advocateLow = advocateHours * 200;
    const advocateHigh = advocateHours * 300;
    const advocateMid = advocateHours * advocateRate;

    // Solicitor for a single demand letter, flat range based on
    // publicly-quoted AU small-claims rates: ~$350 low end, ~$650 with
    // a follow-up, more if they have to read the agent's reply.
    const solicitorLow = amount < 1000 ? 350 : 450;
    const solicitorHigh = amount < 2000 ? 650 : 850;
    const solicitorMid = Math.round((solicitorLow + solicitorHigh) / 2);

    const diyTimeValue = diyHours * HOURLY_TIME_VALUE;

    // Hours TenantTrail saves you (we estimate ~1 hour to upload and review).
    const ttHours = 1;
    const hoursSaved = Math.max(0, diyHours - ttHours);

    // Headline saving: the biggest realistic gap between hiring help and
    // TenantTrail's $69. Pick the most expensive hired-help path so the
    // saving claim is conservative for the visitor's worst case AND
    // dynamic with the dispute size.
    const TT_PRICE = 69;
    const solicitorVsTT = Math.max(0, solicitorMid - TT_PRICE);
    const advocateVsTT = Math.max(0, advocateMid - TT_PRICE);
    const biggestSaving = Math.max(solicitorVsTT, advocateVsTT);
    const biggestSavingLabel = advocateVsTT >= solicitorVsTT
      ? "a private tenancy advocate"
      : "a solicitor's letter";
    // Keep the old "saving" field for backward-compat callers (share modal).
    const saving = biggestSaving;
    return {
      diyHours, diyTimeValue,
      advocateHours, advocateLow, advocateHigh, advocateMid,
      solicitorLow, solicitorHigh, solicitorMid,
      hoursSaved, biggestSaving, biggestSavingLabel,
      saving,
    };
  }, [amount]);

  const fmt = (n) => `$${n.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;

  const presets = [
    { label: "Small repair / 2-week rent", value: 600 },
    { label: "Half a bond claim", value: 1200 },
    { label: "Full bond", value: 2500 },
    { label: "Larger dispute", value: 5000 },
  ];

  return (
    <section className="bg-[#FAF9F4] border-y border-[#E5E4DE]" data-testid="features-roi">
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
        <div className="flex items-center gap-2 mb-2">
          <Calculator size={14} strokeWidth={1.5} className="text-[#1B6C62]" />
          <span className="label-mono text-[10px] text-[#1B6C62]">Worth-it check · honest numbers</span>
        </div>
        <h2 className="font-serif text-2xl sm:text-3xl text-[#0D2B52] mb-2 leading-tight">
          What does going it alone really cost?
        </h2>
        <p className="text-[14px] text-[#5a6b7a] max-w-2xl leading-relaxed mb-6">
          Move the slider to the amount you&apos;re fighting over, a half-bond, a repair bill, a rent rise. We&apos;ll show you what each realistic path would cost in time and dollars, side by side. We don&apos;t promise an outcome, just the honest numbers.
        </p>

        {/* Input */}
        <div className="bg-white border border-[#E5E4DE] p-5 sm:p-6 mb-5" data-testid="roi-input-card">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="label-mono text-[10px] block mb-2">What&apos;s at stake?</label>
              <div className="text-3xl font-serif text-[#0D2B52] mb-2" data-testid="roi-amount-display">{fmt(amount)}</div>
              <input
                type="range" min={100} max={10000} step={100}
                value={amount}
                onChange={e => setAmount(Number(e.target.value))}
                className="w-full accent-[#1B6C62]"
                data-testid="roi-amount-slider"
                aria-label="Dispute amount"
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {presets.map(p => (
                  <button
                    key={p.value} type="button"
                    onClick={() => setAmount(p.value)}
                    className={`text-[11px] px-2 py-1 border ${amount === p.value ? "bg-[#0D2B52] text-white border-[#0D2B52]" : "bg-white text-[#5a6b7a] border-[#E5E4DE] hover:border-[#1B6C62]"}`}
                    data-testid={`roi-preset-${p.value}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label-mono text-[10px] block mb-2">What kind of dispute?</label>
              <div className="grid grid-cols-3 gap-1.5 text-xs">
                {[
                  { id: "bond", label: "Bond" },
                  { id: "repairs", label: "Repairs" },
                  { id: "other", label: "Other" },
                ].map(t => (
                  <button
                    key={t.id} type="button"
                    onClick={() => setDisputeType(t.id)}
                    className={`px-2 py-2 border ${disputeType === t.id ? "bg-[#1B6C62] text-white border-[#1B6C62]" : "bg-white text-[#0D2B52] border-[#E5E4DE] hover:border-[#1B6C62]"}`}
                    data-testid={`roi-type-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-[#6B7280] mt-3 leading-snug italic">
                Estimates are based on publicly-quoted AU tenancy-advocate rates ($200–300/hr) and our own usage data. We don&apos;t promise an outcome, just an honest cost comparison.
              </p>
            </div>
          </div>
        </div>

        {/* Four paths */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5 items-stretch">
          {/* Going it alone */}
          <div className="bg-white border border-[#E5E4DE] p-5 flex flex-col" data-testid="roi-path-diy">
            <div className="label-mono text-[10px] text-[#6B7280] mb-2">Path 1 · DIY</div>
            <h3 className="font-serif text-xl text-[#0D2B52] mb-3">Going it alone</h3>
            <div className="space-y-2 mb-4">
              <RoiRow label="Hours organising & drafting" value={`~${estimates.diyHours} hrs`} />
              <RoiRow label="Free legal advice waitlist" value="3–4 weeks" />
              <RoiRow label="Letters from scratch" value="Yes" />
              <RoiRow label="Tribunal-ready bundle" value="DIY in Word" />
            </div>
            <div className="mt-auto border-t border-[#E5E4DE] pt-3">
              <div className="text-[11px] text-[#6B7280] mb-0.5">Your time at ${HOURLY_TIME_VALUE}/hr</div>
              <div className="font-serif text-2xl text-[#0D2B52]" data-testid="roi-diy-cost">{fmt(estimates.diyTimeValue)}</div>
            </div>
          </div>

          {/* Solicitor, single letter */}
          <div className="bg-white border border-[#E5E4DE] p-5 flex flex-col" data-testid="roi-path-solicitor">
            <div className="label-mono text-[10px] text-[#6B7280] mb-2">Path 2 · Solicitor</div>
            <h3 className="font-serif text-xl text-[#0D2B52] mb-3">One lawyer letter</h3>
            <div className="space-y-2 mb-4">
              <RoiRow label="Demand letter on letterhead" value="Yes" />
              <RoiRow label="Read your story, draft, send" value="1–2 days" />
              <RoiRow label="Tribunal representation" value="Extra ($$$)" />
              <RoiRow label="Evidence + photo prep" value="You still do" />
            </div>
            <div className="mt-auto border-t border-[#E5E4DE] pt-3">
              <div className="text-[11px] text-[#6B7280] mb-0.5">AU solicitor flat-fee range</div>
              <div className="font-serif text-2xl text-[#0D2B52]" data-testid="roi-solicitor-cost">{fmt(estimates.solicitorLow)}–{fmt(estimates.solicitorHigh)}</div>
            </div>
          </div>

          {/* Private advocate */}
          <div className="bg-white border border-[#E5E4DE] p-5 flex flex-col" data-testid="roi-path-advocate">
            <div className="label-mono text-[10px] text-[#6B7280] mb-2">Path 3 · Advocate</div>
            <h3 className="font-serif text-xl text-[#0D2B52] mb-3">Tenancy advocate</h3>
            <div className="space-y-2 mb-4">
              <RoiRow label="Hourly rate (AU range)" value="$200–300/hr" />
              <RoiRow label="Typical prep hours" value={`${estimates.advocateHours} hrs`} />
              <RoiRow label="Letters drafted" value="Included" />
              <RoiRow label="Tribunal representation" value="Often extra" />
            </div>
            <div className="mt-auto border-t border-[#E5E4DE] pt-3">
              <div className="text-[11px] text-[#6B7280] mb-0.5">Estimated range</div>
              <div className="font-serif text-2xl text-[#0D2B52]" data-testid="roi-advocate-cost">{fmt(estimates.advocateLow)}–{fmt(estimates.advocateHigh)}</div>
            </div>
          </div>

          {/* TenantTrail */}
          <div className="bg-gradient-to-br from-[#0D2B52] to-[#1B6C62] text-white p-5 relative ring-2 ring-[#C9A961] flex flex-col" data-testid="roi-path-tt">
            <div className="absolute top-3 right-3 text-[9px] font-mono uppercase tracking-wide bg-[#C9A961] text-white px-1.5 py-0.5">
              Same day
            </div>
            <div className="label-mono text-[10px] text-white/60 mb-2">Path 4 · Compass</div>
            <h3 className="font-serif text-xl text-white mb-3">TenantTrail Dispute Mode</h3>
            <div className="space-y-2 mb-4 text-white/95">
              <RoiRow label="500 AI Q&As over 30 days" value="Included" dark />
              <RoiRow label="11 letter templates" value="Included" dark />
              <RoiRow label="Evidence bundle PDF" value="One-click" dark />
              <RoiRow label="Photo Compare + Trail" value="Included" dark />
            </div>
            <div className="mt-auto border-t border-white/20 pt-3">
              <div className="text-[11px] text-white/60 mb-0.5">One-off, no subscription</div>
              <div className="font-serif text-2xl text-white" data-testid="roi-tt-cost">$69</div>
            </div>
            <Link
              to="/pricing#dispute"
              className="mt-4 inline-flex items-center gap-1.5 bg-white text-[#0D2B52] px-3.5 py-2 text-xs font-medium hover:bg-[#FAF9F4] transition-colors"
              data-testid="roi-tt-cta"
            >
              Get Dispute Mode <ArrowRight size={12} strokeWidth={1.75} />
            </Link>
          </div>
        </div>

        {/* Dynamic savings + time-saved band. Numbers move with the slider. */}
        {estimates.biggestSaving >= 100 && (
          <div className="mb-4 bg-[#FAF6E8] border border-[#C9A961]/40 p-5 sm:p-6" data-testid="roi-share-trigger">
            <div className="flex items-start gap-2.5 mb-3">
              <Sparkles size={16} strokeWidth={1.5} className="text-[#8A6D3B] mt-0.5 shrink-0" />
              <div className="font-serif text-[15px] sm:text-[16px] text-[#0D2B52]">
                For a <strong>{fmt(amount)}</strong> dispute, TenantTrail does the heavy lifting for <strong>$69</strong>.
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 sm:gap-4 mb-4">
              <div className="bg-white/70 border border-[#C9A961]/30 p-3">
                <div className="label-mono text-[9.5px] text-[#8A6D3B] mb-1">Money saved</div>
                <div className="font-serif text-2xl text-[#0D2B52]" data-testid="roi-money-saved">{fmt(estimates.biggestSaving)}</div>
                <div className="text-[11px] text-[#6B7280] leading-snug mt-0.5">vs {estimates.biggestSavingLabel}</div>
              </div>
              <div className="bg-white/70 border border-[#C9A961]/30 p-3">
                <div className="label-mono text-[9.5px] text-[#8A6D3B] mb-1">Time saved</div>
                <div className="font-serif text-2xl text-[#0D2B52]" data-testid="roi-time-saved">~{estimates.hoursSaved} hrs</div>
                <div className="text-[11px] text-[#6B7280] leading-snug mt-0.5">of admin Compass handles for you</div>
              </div>
              <div className="bg-white/70 border border-[#C9A961]/30 p-3">
                <div className="label-mono text-[9.5px] text-[#8A6D3B] mb-1">Ready in</div>
                <div className="font-serif text-2xl text-[#0D2B52]">Same day</div>
                <div className="text-[11px] text-[#6B7280] leading-snug mt-0.5">letters, bundle and chat</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-[12px] text-[#6B7280] leading-snug max-w-md">
                Help a mate. Share the comparison in your tenancy Facebook group, someone there is probably wondering the same thing.
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to="/pricing#dispute"
                  className="bg-[#1B6C62] hover:bg-[#0D2B52] text-white text-sm px-3.5 py-2 inline-flex items-center gap-1.5 transition-colors"
                  data-testid="roi-band-buy-btn"
                >
                  Get Dispute Mode <ArrowRight size={13} strokeWidth={1.75} />
                </Link>
                <button
                  onClick={() => setShowShare(true)}
                  className="border border-[#C9A961]/60 text-[#8A6D3B] hover:bg-white text-sm px-3.5 py-2 inline-flex items-center gap-1.5 transition-colors"
                  data-testid="roi-share-btn"
                >
                  <Share2 size={13} strokeWidth={1.75} /> Share this
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Honest disclaimer */}
        <div className="bg-white border border-[#E5E4DE] p-4 text-[12px] text-[#5a6b7a] leading-relaxed flex items-start gap-2.5" data-testid="roi-disclaimer">
          <AlertCircle size={14} strokeWidth={1.5} className="text-[#5a6b7a] mt-0.5 shrink-0" />
          <p>
            <strong className="text-[#0D2B52]">This is a cost comparison, not legal advice.</strong> Free community legal centres are excellent and free (just often booked out). Private tenancy advocates can be worth every cent for complex cases. TenantTrail is built to help you do the documentation and the drafting calmly and properly, so whichever path you choose next, you walk in prepared.
          </p>
        </div>
      </div>

      {showShare && (
        <RoiShareModal
          amount={amount}
          disputeType={disputeType}
          saving={estimates.saving}
          onClose={() => setShowShare(false)}
        />
      )}
    </section>
  );
}

// ---------- ROI share modal ----------
function RoiShareModal({ amount, disputeType, saving, onClose }) {
  const [copied, setCopied] = React.useState(false);
  const cardUrl = `${API}/public/roi/card.jpg?amount=${amount}&type=${disputeType}`;
  const pageUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/features`;
  const fmt = (n) => `$${n.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
  const tweet = `I just compared what a ${fmt(amount)} rental dispute would cost three ways. TenantTrail's option saved me ≈ ${fmt(saving)} vs hiring help. Worth a look if you rent in 🇦🇺`;
  const shareUrl = encodeURIComponent(pageUrl);
  const shareText = encodeURIComponent(tweet);

  const copyLink = () => {
    navigator.clipboard.writeText(`${tweet}\n${pageUrl}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const downloadImage = async () => {
    try {
      const r = await fetch(cardUrl);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tenanttrail-roi-${amount}.jpg`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      window.open(cardUrl, "_blank");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="roi-share-modal"
    >
      <div
        className="bg-[#FAF9F4] max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[#0D2B52] text-white px-5 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity" data-testid="roi-share-brand">
            <Logo size={22} />
            <div className="leading-tight">
              <div className="font-serif text-base text-white">TenantTrail</div>
              <div className="font-mono text-[9px] text-white/60 uppercase tracking-wider">tenanttrail.net</div>
            </div>
          </a>
          <div className="flex items-center gap-2">
            <Share2 size={14} strokeWidth={1.5} className="text-white/80" />
            <span className="font-serif text-base text-white/80">Share this comparison</span>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white p-1 ml-2"
              data-testid="roi-share-close"
            ><XIcon size={16} strokeWidth={1.75} /></button>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <p className="text-[13px] text-[#5a6b7a] mb-4">
            Right-click and save the image below, or use a share button. The URL{" "}
            <a href="/features" className="text-[#1B6C62] hover:underline" data-testid="roi-share-link">tenanttrail.net/features</a>
            {" "}is baked into the image so it works even when Facebook strips link previews.
          </p>

          {/* Preview */}
          <a
            href={cardUrl}
            target="_blank" rel="noopener noreferrer"
            className="block border border-[#E5E4DE] bg-white shadow-sm hover:shadow-md mb-4 overflow-hidden"
          >
            <img
              src={cardUrl}
              alt="ROI comparison share card"
              className="w-full h-auto"
              data-testid="roi-share-preview"
            />
          </a>

          {/* Action row */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={downloadImage}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1B6C62] text-white text-sm hover:opacity-90"
              data-testid="roi-share-download"
            >
              <Download size={13} strokeWidth={1.75} /> Save image
            </button>
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-[#E5E4DE] text-[#0D2B52] text-sm hover:bg-[#F4F3ED]"
              data-testid="roi-share-copy"
            >
              {copied ? (<><Check size={13} strokeWidth={2} /> Copied text + link</>) : (<><Copy size={13} strokeWidth={1.75} /> Copy text + link</>)}
            </button>
          </div>

          {/* Social */}
          <div className="label-mono text-[10px] mb-1.5">Share to</div>
          <div className="flex flex-wrap gap-1.5 mb-4">
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1877F2] text-white text-sm hover:opacity-90"
              data-testid="roi-share-fb"
            ><Facebook size={13} strokeWidth={1.75} /> Facebook</a>
            <a
              href={`https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-black text-white text-sm hover:opacity-90"
              data-testid="roi-share-x"
            ><Twitter size={13} strokeWidth={1.75} /> X</a>
            <a
              href={`https://wa.me/?text=${shareText}%20${shareUrl}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#25D366] text-white text-sm hover:opacity-90"
              data-testid="roi-share-whatsapp"
            ><MessageCircle size={13} strokeWidth={1.75} /> WhatsApp</a>
          </div>

          <p className="text-[11px] text-[#6B7280] italic">
            The image is always anonymous, no personal information is used to generate it. The URL it points to is just{" "}
            <a href="/features" className="text-[#1B6C62] hover:underline" data-testid="roi-share-link-bottom">tenanttrail.net/features</a>, where anyone can rerun the numbers themselves.
          </p>
        </div>
      </div>
    </div>
  );
}

function RoiRow({ label, value, dark }) {
  return (
    <div className="flex items-start gap-3 text-[12.5px] leading-snug">
      <span className={`flex-1 ${dark ? "text-white/80" : "text-[#6B7280]"}`}>{label}</span>
      <span className={`font-mono text-[12px] text-right shrink-0 min-w-[88px] ${dark ? "text-white" : "text-[#0D2B52]"}`}>{value}</span>
    </div>
  );
}
